import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, Mail, MapPin, MessageCircle, Phone, Star } from "lucide-react";
import { site } from "@/data/site";
import { reviewPlatforms } from "@/data/reviews";
import { services } from "@/data/services";
import { regions } from "@/data/regions";
import { Section, SectionHeading } from "@/components/site/Section";
import { QuoteForm } from "@/components/site/QuoteForm";
import { JsonLd } from "@/components/site/JsonLd";
import { ReviewBar } from "@/components/site/ReviewBar";
import { FaqBlock } from "@/components/site/FaqBlock";
import { ReviewCta } from "@/components/site/ReviewCta";
import { localBusinessSchema, breadcrumbSchema, faqSchema, meta } from "@/lib/schema";
import { overviewKeywords } from "@/lib/localKeywords";

const title = "Contact Vakkundig Verhuizingen & Transport | Zuid-Holland";
const description =
  "Neem contact op met Vakkundig Verhuizingen & Transport: bel 085 060 1023, WhatsApp 06 84566663 of mail. Dagelijks 09:00–18:00 bereikbaar in heel Zuid-Holland en Nederland.";

const contactFaq = [
  {
    q: "Hoe snel krijg ik antwoord op mijn aanvraag?",
    a: "Bel of WhatsApp ons en je hebt meestal direct iemand aan de lijn. Aanvragen via het formulier of per e-mail beantwoorden wij doorgaans binnen één werkdag met een prijsindicatie of een voorstel voor een gratis opname.",
  },
  {
    q: "Kan ik langskomen op jullie locatie in Voorburg?",
    a: "Onze vestiging staat aan de Van Naeltwijckstraat 114A in Voorburg (Leidschendam-Voorburg), direct naast Den Haag. Kom je liever langs voor verhuismateriaal of een gesprek? Bel of WhatsApp ons vooraf, dan weten we zeker dat er iemand voor je klaarstaat.",
  },
  {
    q: "In welke plaatsen zijn jullie actief?",
    a: "Wij verhuizen door heel Nederland, met de meeste opdrachten in Den Haag, Rijswijk, Delft, Zoetermeer, Leidschendam-Voorburg, Wassenaar, Westland, Rotterdam en Leiden. Ook internationale verhuizingen zijn mogelijk.",
  },
  {
    q: "Kan ik contact opnemen voor een spoedverhuizing?",
    a: "Ja. Bij spoed kunnen wij vaak nog dezelfde week schakelen en soms binnen 24 uur. Bel ons direct, dan kijken we meteen in de planning.",
  },
  {
    q: "Krijg ik een gratis en vrijblijvende offerte?",
    a: "Ja. Een opname en offerte zijn altijd gratis en vrijblijvend. Je ontvangt een vaste prijs met een duidelijke omschrijving van wat er wel en niet bij zit.",
  },
];

export const Route = createFileRoute("/contact")({
  head: () => meta({
      title,
      description,
      path: "/contact",
      image: "team",
      keywords: overviewKeywords(["verhuisbedrijf contact", "verhuisbedrijf den haag telefoonnummer", "verhuizers bellen"]),
    }),
  component: ContactPage,
});

function ContactPage() {
  const topRegions = regions.slice(0, 12);

  return (
    <>
      <JsonLd data={localBusinessSchema} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <JsonLd data={faqSchema(contactFaq)} />

      <Section tone="navy">
        <SectionHeading
          as="h1"
          eyebrow="Contact"
          title="Contact met je verhuisbedrijf Vakkundig Verhuizingen & Transport"
          intro="Je kunt ons vrijblijvend bellen of mailen — ons vakkundige team staat klaar om je te helpen. We denken graag met je mee, ook als je verhuizing nog niet definitief is. Zeven dagen per week bereikbaar en actief door heel Nederland."
          inverted
        />

      </Section>

      <ReviewBar />


      <Section>
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <ul className="space-y-5">
              <li className="flex gap-3">
                <Phone className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Telefoon
                  </span>
                  <a href={site.phoneHref} className="text-lg font-bold text-secondary hover:text-brand">
                    {site.phone}
                  </a>
                </span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    WhatsApp
                  </span>
                  <a
                    href={site.whatsappMessage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-bold text-secondary hover:text-brand"
                  >
                    {site.whatsapp}
                  </a>
                </span>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    E-mail
                  </span>
                  <a href={site.emailHref} className="break-all font-semibold text-secondary hover:text-brand">
                    {site.email}
                  </a>
                </span>
              </li>
              <li className="flex gap-3">
                <MapPin className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Adres
                  </span>
                  <span className="font-semibold text-secondary">
                    {site.address.street}, {site.address.postalCode} {site.address.city}
                  </span>
                </span>
              </li>
              <li className="flex gap-3">
                <Clock className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Openingstijden
                  </span>
                  <span className="font-semibold text-secondary">{site.hours}</span>
                </span>
              </li>
              <li className="flex gap-3">
                <Star className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Google Bedrijfsprofiel
                  </span>
                  <a
                    href={site.social.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-secondary hover:text-brand"
                  >
                    Bekijk ons profiel en onze reviews op Google
                  </a>
                </span>
              </li>
            </ul>


            <div className="mt-8 overflow-hidden rounded-xl border border-border">
              <iframe
                title="Locatie Vakkundig Verhuizingen & Transport in Voorburg"
                src="https://www.google.com/maps?q=Van+Naeltwijckstraat+114A,+2274+PD+Voorburg&output=embed"
                loading="lazy"
                className="h-72 w-full border-0"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-background p-6 shadow-[var(--shadow-card)] sm:p-8">
            <h2 className="font-display text-2xl font-extrabold text-secondary">Stuur ons een bericht</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Vul het formulier in en we nemen zo snel mogelijk contact met je op.
            </p>
            <div className="mt-5">
              <QuoteForm />
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Waar je ons vindt"
          title="Onze profielen en reviews op alle platformen"
          intro="Wij staan met dezelfde naam, hetzelfde adres en hetzelfde telefoonnummer op elk platform. Lees onze beoordelingen bij de bron of bekijk onze foto's op social media."
        />
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ...reviewPlatforms.map((p) => ({
              name: `${p.name} — ${p.count}`,
              url: p.url,
            })),
            { name: "Instagram — onze foto's", url: site.social.instagram },
            { name: "Facebook — onze foto's", url: site.social.facebook },
            { name: "LinkedIn — zakelijke verhuizingen", url: site.social.linkedin },
            { name: "TikTok — onze video's", url: site.social.tiktok },
          ].map((item) => (
            <a
              key={item.name}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border border-border bg-background px-5 py-4 text-sm font-bold text-secondary transition hover:border-brand hover:text-brand"
            >
              {item.name}
            </a>
          ))}
        </div>
      </Section>



      <Section tone="muted">
        <SectionHeading
          eyebrow="Bereikbaarheid"
          title="Verhuisbedrijf in Den Haag, actief in heel Nederland"
        />
        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <article>
            <h3 className="font-display text-xl font-bold text-secondary">
              Vanuit Den Haag naar elke bestemming
            </h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Onze vestiging staat aan de Van Naeltwijckstraat 114A in Voorburg, direct naast Den Haag en midden in Zuid-Holland. Daardoor zijn wij snel ter
              plaatse in Den Haag, Rijswijk, Delft, Zoetermeer, Leidschendam-Voorburg, Wassenaar, Westland,
              Voorschoten, Rotterdam, Schiedam, Vlaardingen, Leiden en Gouda. Verhuis je verder weg, naar Amsterdam,
              Utrecht, Eindhoven of Groningen, of zelfs naar het buitenland? Ook dan rijden onze bakwagens met eigen
              team en materiaal voor je uit.
            </p>
          </article>
          <article>
            <h3 className="font-display text-xl font-bold text-secondary">
              Zeven dagen per week persoonlijk contact
            </h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Bij Vakkundig Verhuizingen &amp; Transport spreek je altijd iemand die jouw verhuizing van begin tot eind
              kent. Eén vast aanspreekpunt, korte lijnen en duidelijke afspraken: bel {site.phone} of stuur ons je
              adres, verhuisdatum en een overzicht van je inboedel per e-mail. Wij zijn dagelijks van 09:00 tot 18:00
              bereikbaar, ook in het weekend, en denken graag met je mee over de planning, de inzet van een
              verhuislift, montage en demontage en tijdelijke opslag. Zo weet je precies waar je aan toe bent — rustig,
              professioneel en vakkundig geregeld.
            </p>
          </article>

        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Waarvoor kun je ons bellen"
          title="Neem contact op over deze diensten"
          intro="Weet je niet zeker onder welke dienst jouw klus valt? Bel of WhatsApp ons gerust, dan kijken we samen wat je nodig hebt."
        />
        <div className="mt-8 flex flex-wrap gap-2">
          {services.map((s) => (
            <Link
              key={s.slug}
              to="/diensten/$slug"
              params={{ slug: s.slug }}
              className="rounded-full border border-border bg-muted px-4 py-2 text-sm font-bold text-secondary transition hover:border-brand hover:text-brand"
            >
              {s.title} →
            </Link>
          ))}
        </div>

        <h3 className="mt-10 font-display text-xl font-bold text-secondary">
          Verhuisbedrijf in jouw plaats
        </h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {topRegions.map((region) => (
            <Link
              key={region.slug}
              to="/werkgebied/$slug"
              params={{ slug: region.slug }}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-secondary transition hover:border-brand hover:text-brand"
            >
              Verhuisbedrijf {region.city}
            </Link>
          ))}
          <Link
            to="/werkgebied"
            className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand"
          >
            Alle werkgebieden →
          </Link>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading eyebrow="Veelgestelde vragen" title="Vragen over contact en afspraken" />
        <div className="mt-8">
          <FaqBlock items={contactFaq} />
        </div>
        <div className="mt-10 max-w-2xl">
          <ReviewCta />
        </div>
      </Section>
    </>
  );
}

